import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type CoMingleReturnProps, toReturnShipper, withCoMingleReturns } from '@/pages/return/coMingle'
import { flushSync } from 'react-dom'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { core } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import dayjs from 'dayjs'
import { MediaModal } from '@/components/pbo/MediaModal'
import { callToAddImportLog } from '@/services/excel/apis'
import { failureError, invalidError, notice, rowInvalidError } from '@/components/pbo/notice'
import { FETCH_ALL_MESSAGES, guardFetching, noticeIfUploadExceedsPage } from '@/hooks/useFetchAllRows'
import { withNewMarker, withoutNewMarker } from '@/components/pbo/newRowMarker'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { SkuUnmappedModal } from '@/pages/inbound/generalInbound/components/SkuUnmappedModal'
import { asSkuUnmapped, type SkuUnmappedError } from '@/pages/inbound/generalInbound/queries/useModifyInbounds'
import { isValidDownloadPassword } from '@/pages/return/rgr10V2/components/ExcelDownloadModal'
import { excludeAllOption, toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import type { ComboOption } from '@/services/combo/types'
import { getCommonFiles } from '@/services/commonFile/apis'
import type { CommonFileItemRes } from '@/services/commonFile/types'
import { getShipperListV2 } from '@/services/shipper/apis'
import type { ShipperItem } from '@/services/shipper/types'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { COLUMN_META } from './columnMeta'
import { ExcelImportError, makeCodeMapper, parseReturnInboundExcel } from './components/excelImport'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnInboundPageView, { type CellTarget, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import {
  DELETE_IDS_MAX,
  downloadReturnInboundsExcel,
  mixedShippers,
  type RequiredField,
  saveShipper,
  useConfirmReturnInbounds,
  useDeleteReturnInbounds,
  useSaveReturnInbounds,
  validateRows,
} from './queries/useModifyReturnInbounds'
import {
  type BarcodeOption,
  emptyReturnInboundRow,
  fetchBarcodeOptions,
  followBarcode,
  hasShipperCondition,
  isReturnItem,
  returnInboundQueryKeys,
  type ReturnArrivalNoticeItem,
  type ReturnInboundParams,
  type ReturnInboundRow,
  toRow,
  useReturnInboundList,
} from './queries/useReturnInboundList'
import { fromSearchQuery, type Rgr01SearchQuery, toSearchQuery } from './searchQuery'
import { applyEtc5Setting, ETC5_KEY, useReturnInboundEtc5Setting } from './queries/useReturnInboundEtc5Setting'
import { openFreeTextColumns } from './editableColumns'

/** 화주유형 선택지 — 1.0 과 같은 6종(생성본 데모 옵션과 같은 값). 화주 고정 계정은 자기 유형 하나만 준다 */
const SHIPPER_TYPE_OPTIONS: Option[] = ['M1P', 'MFS', 'MWP', 'MLT', 'MCD', 'MTP'].map((v) => ({ value: v, label: v }))
/** 화주유형 옵션 빈 목록 — 옵션 슬롯은 렌더마다 불리므로 참조를 고정한다. */
const EMPTY_OPTIONS: Option[] = []
/** 확인창을 연 직후 같은 버튼을 다시 눌러도 또 열지 않는 시간(ms) — 더블클릭 간격보다 넉넉히 */
const CONFIRM_REOPEN_GUARD_MS = 500
/** LOGISPRD-14924에서 확정한 INOOINT 반품입고 보호 계정. 다른 화주는 제한하지 않는다. */
const PROTECTED_ACCOUNT_EMAIL = 'pdj@musinsalogistics.co.kr'
/** 상세사유 최소 길이 — BE `detailedReason.length >= 5` */
const REASON_DETAIL_MIN_LENGTH = 5
/** 업로드 헤더 중 표 제목과 다른 이름 — BE 다운로드 양식(ReturnArrivalNoticeExcelDto)은 입고센터를 '주문센터' 로 쓴다 */
const EXCEL_HEADER_ALIASES: [string, string][] = [['주문센터', 'WH_NM']]
/** 업로드로 받지 않는 컬럼 — 첨부·동영상 개수와 생성·수정 이력 */
const NOT_IMPORTABLE = new Set(['ATCH_FILE', 'VIDEO', 'INS_DATE', 'INS_BY', 'UPD_DATE', 'UPD_BY'])

/** 저장 전 필수 컬럼 — 생성본 COLUMNS 의 required 사본(columnMeta.ts). 표의 별표와 검사가 갈라지지 않게 */
const REQUIRED_FIELDS: RequiredField[] = COLUMN_META.filter((c) => c.required).map((c) => ({ key: c.key as keyof ReturnInboundRow, title: c.title }))
/** 업로드 헤더(표 제목) → 컬럼 키 */
const TITLE_TO_KEY = new Map<string, string>([
  ...COLUMN_META.filter((c) => !NOT_IMPORTABLE.has(c.key)).map((c): [string, string] => [c.title, c.key]),
  ...EXCEL_HEADER_ALIASES,
])

/** 입고예정일자 기본 기간 — 1.0 sub.js:519-520 `[오늘-7일, 오늘]`(PM 확인 2026-09-15). 데모 기본(오늘)과 다른 의도적 이탈 */
const defaultPlnDate = (): [string, string] => [dayjs().subtract(7, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]

/** 콤보 옵션 → 셀 옵션. 콤보 코드 'NULL'(온라인·양품)은 원장이 빈 값이라 '' 로 맞춘다 */
const toCodeOptions = (options: ComboOption[] | undefined) =>
  toSelectOptions(options).map((o) => ({ ...o, value: o.value.toUpperCase() === 'NULL' ? '' : o.value }))

const apiErrMessage = (e: unknown) => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  if (resp?.data?.meta?.message) return resp.data.meta.message
  if (resp?.status) return `처리에 실패했습니다. (HTTP ${resp.status})`
  // 화면 가드가 던진 Error(화주 혼재 등)도 여기로 온다 — 그 문구를 살린다
  if (e instanceof Error && e.message.trim()) return e.message
  return '처리에 실패했습니다.'
}

/** 엑셀 다운로드 오류는 blob 으로 온다 — 본문 JSON 의 meta.message(비밀번호 규칙 등)를 꺼낸다 */
const blobErrMessage = async (e: unknown) => {
  const data = (e as { response?: { data?: unknown } })?.response?.data
  if (data instanceof Blob) {
    try {
      const message = (JSON.parse(await data.text()) as { meta?: { message?: string } })?.meta?.message
      if (message) return String(message)
    } catch {
      // 본문이 JSON 이 아니면 상태 코드 문구로 떨어진다
    }
  }
  return apiErrMessage(e)
}

/**
 * 실패 = throw(뷰 계약). 안내는 `invalidError`·`rowInvalidError`·`failureError`(공용 notice — 검증 토스트·행 짚는 검증/실패 알럿)가 띄운다.
 * 작업 모달(업로드)은 열린 채 남고, [확인]을 누르는 즉시 닫히는 확인창(확정·삭제)과 다운로드 모달(okClosesFirst — 옛 화면과 같다)은 이미 닫혀 있다.
 * 어느 쪽이든 표 선택은 남는다. 버튼 슬롯의 throw 는 조용히 삼킨다.
 *
 * `failAndClose` — 안내가 끝난 오류를 **모달을 닫는** 실패로 던진다. 옛 화면이 모달을 닫고 나서 처리하던 단계(업로드 파일 읽기)의 실패.
 * 뷰는 던진 값의 closeModal 을 보고 닫는다.
 */
const failAndClose = (error: Error): never => {
  throw Object.assign(error, { closeModal: true })
}

const list = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map((x) => String((x ?? '') as string | number)).filter(Boolean)
  return typeof v === 'string' && v ? [v] : []
}
const text = (v: unknown): string => (typeof v === 'string' ? v : '')

/**
 * 조회영역 값 ↔ 저장(세션·URL) 형태 — 송장 칸의 키가 다르다: 생성본은 데모 키 `REF_NO3`, 저장·BE 조건은 `REF_NO2`
 * (1.0 RGR01 필터 설정과 원장 실측 모두 송장번호가 REF_NO2 — PM 확인 2026-09-15). 화면 키는 데모를 따르고 여기서만 옮긴다.
 */
const toStored = (fl: Record<string, unknown>): Rgr01SearchQuery => toSearchQuery({ ...fl, REF_NO2: fl.REF_NO3 })
const fromStored = (q: Record<string, unknown>): Record<string, unknown> | undefined => {
  const restored = fromSearchQuery(q)
  if (!restored) return undefined
  const { REF_NO2, ...rest } = restored
  return REF_NO2 ? { ...rest, REF_NO3: REF_NO2 } : rest
}

/**
 * RGR01 반품입고 2.0 (dev 라우트 rgr16) — 컴포넌트 모드(컨덕터 문서 51).
 *
 * 겉(조회영역·표·액션바·모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고, 이 파일은 옛 훅·API·검증을 그 뷰에
 * 배선(Wiring)으로 넘긴다. 체크 선택·목록 검색·페이저·모달 열림·헬퍼 모달 조건은 뷰가 가진다.
 *
 * 데모 대비 의도적 이탈(근거는 각 지점 주석):
 * - 입고예정일자 기본 = 오늘-7일~오늘(PM 확인 2026-09-15). 데모는 오늘.
 * - 송장 필터 키: 화면은 데모 키 REF_NO3, BE 조건·저장은 REF_NO2.
 * - 첨부·동영상은 뷰의 미디어 모달(core.MediaManager — 파일 이름뿐) 대신 공용 MediaModal(서버 업로드·내려받기)을 연다
 *   (`cellOpens` 가 false — PD 결정 2026-09-30 "현재 dev 유지").
 * - 옵션은 콤보(거래유형·불량구분·입고센터·사유코드)·화주 API. 데모 상수는 화면 설계용.
 *
 * 옛 화면 동작을 유지하는 자리(PD 결정 2026-09-30 "유지" · "개발이 의도한 동작은 데모에 맞춘다"):
 * - 데모(샘플)에 실은 것: [조회]는 목록 검색어 유지 · [초기화]는 조회 조건만 · 확인창·다운로드는 누르면 닫고 뒤에서 처리 · 다운로드 [확인]은 입력이 유효할 때만 활성 · 확인창 건수 문구 · 수량 10자리 · 입고센터 셀 검색 · 화주유형 변경 시 화주명 비움·유형 지우기(×) · 목록 검색 시 선택 비움·1페이지 · 헬퍼 모달 Enter 조회.
 * - 엑셀 업로드 뒤 가져온 행을 전부 체크한다(`selectAllAfterUpload`) — 검토 후 바로 [저장].
 * - 화주 고정 계정은 화주유형·화주명 칸을 잠근다(`filterDisabled`). 값은 기본값·조회 슬롯에서 자기 화주로 고정한다.
 *
 * 옛 화면과 달라진 뷰 동작(생성본·데모 규약, 배선에서 못 바꾼다 — PR 본문에 적음):
 * - 헬퍼 모달 목록 높이는 뷰 기본값을 따른다.
 * - 1만 행을 넘으면: 업로드 뒤 전체 체크·체크 유지가 현재 페이지에 한정된다(DS 표의 선택 보고가 보이는 행 기준 — DS 수정 대기).
 */
function ReturnInboundPage({ coMingleEnabled }: CoMingleReturnProps) {
  const queryClient = useQueryClient()

  // ── 로그인 화주 ─────────────────────────────────────────────
  // 재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르고 기본값은 MUSINSA 다. 고른 화주 그대로 조회한다(LOGISPRD-15725, `withCoMingle`)
  const { ownShipperCode, ownShipperName, isFreeShipper, scope, selectableNames, defaultShipperName } = useMemo(() => {
    const user = AuthStorage.user.get()
    const scope = withCoMingle(shipperScopeOf(user), coMingleEnabled) // 잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652)
    const ownShipperName = String(user.shipperName ?? '')
    // 화주 목록 요청·화주명 값은 화주명이다 — 자기 화주는 세션 화주명(shipperName)이다(gr02V2 준용)
    const nameOf = (code: string, name: string) => (code === scope.shipperCode ? ownShipperName : name)
    return {
      ownShipperCode: scope.shipperCode,
      ownShipperName,
      isFreeShipper: !scope.locked,
      scope,
      selectableNames: selectableShippers(scope).map((s) => nameOf(s.shipperCode, s.shipperName)).filter(Boolean),
      defaultShipperName: nameOf(defaultShipper(scope).shipperCode, defaultShipper(scope).shipperName),
    }
  }, [coMingleEnabled])
  /** 화주 헬퍼 모달은 저장할 행의 화주를 찾는다 — 재고통합 ON 이면 지금처럼 MUSINSA 로 찾는다(조회 경로 밖, LOGISPRD-15725) */
  const helperShipperName = coMingleEnabled && toReturnShipper(ownShipperCode, true) === 'MUSINSA' ? 'MUSINSA' : ownShipperName
  /** 화주 목록 — 화주명 옵션과 화주유형을 얻는다. 화주 고정 계정은 고를 수 있는 화주만 받는다(rgr10V2 준용) */
  const { data: shippers = [], isFetched: shippersFetched } = useQuery<ShipperItem[]>({
    queryKey: ['rgr16', 'SHIPPERS', isFreeShipper ? 'ALL' : selectableNames.join(',')],
    queryFn: async () =>
      (await getShipperListV2({ shipperNames: isFreeShipper ? [] : selectableNames, logisticsServiceTypes: [], page: 0, size: 10000 }))?.data?.items ?? [],
    enabled: isFreeShipper || selectableNames.length > 0,
    staleTime: 1000 * 60 * 10,
  })
  /**
   * [일괄 입력] 화주코드 선택지 — 화주 헬퍼가 고를 수 있는 화주와 같다(고정 계정은 helperShipperName 하나, 재고통합 ON 이면 MUSINSA).
   * 화주 헬퍼 [선택]과 같은 세 칸(화주코드·화주명·화주유형)을 채운다(LOGISPRD-15530)
   */
  const helperShipperCode = helperShipperName === 'MUSINSA' ? 'MUSINSA' : ownShipperCode
  const bulkShippers = useMemo(() => (isFreeShipper ? shippers : shippers.filter((s) => s.shipperCode === helperShipperCode)), [isFreeShipper, shippers, helperShipperCode])
  const bulkShipperOptions = useMemo(() => bulkShippers.map((s) => ({ value: s.shipperCode, label: `${s.shipperName}(${s.shipperCode})` })), [bulkShippers])
  const bulkShipperPatch = useCallback(
    (code: string) => {
      const s = bulkShippers.find((it) => it.shipperCode === code)
      return { SPR_NM: code, shipper_name: s?.shipperName ?? '', service_type: s?.logisticsServiceType ?? '' }
    },
    [bulkShippers],
  )
  const ownServiceType = useMemo(() => shippers.find((s) => s.shipperCode === ownShipperCode)?.logisticsServiceType ?? '', [shippers, ownShipperCode])
  /** 화주 고정 계정의 화주명 값 — 고를 수 있는 화주만 남기고, 하나도 없으면 기본 화주다 */
  const lockedShipperNames = useCallback(
    (names: string[]): string[] => {
      const kept = [...new Set(names.filter((n) => selectableNames.includes(n)))]
      if (kept.length) return kept
      return defaultShipperName ? [defaultShipperName] : []
    },
    [selectableNames, defaultShipperName],
  )
  /** 화주 고정 계정이 고른 화주의 유형 — 자기 화주는 코드로, 그 밖(MUSINSA)은 이름으로 목록에서 찾는다. 목록이 오기 전엔 빈 목록 */
  const lockedServiceTypes = useCallback(
    (names: string[]): string[] => [
      ...new Set(
        lockedShipperNames(names)
          .map((n) => (n === ownShipperName ? ownServiceType : shippers.find((s) => s.shipperName === n)?.logisticsServiceType ?? ''))
          .filter(Boolean),
      ),
    ],
    [lockedShipperNames, ownShipperName, ownServiceType, shippers],
  )

  // ── 코드성 옵션(GET /api/v1/combos) ─────────────────────────
  const { data: combos } = useGetCombos(['WH_NM', 'LOT_ATTR4', 'LOT_ATTR5', 'DOWNLOAD_CD', 'ETC5'], { errorToast: true })
  const lot4Options = useMemo(() => toCodeOptions(combos?.LOT_ATTR4), [combos])
  const lot5Options = useMemo(() => toCodeOptions(combos?.LOT_ATTR5), [combos])
  // 정불구분 — 1.0 RGR01 처럼 보는 사람의 화주 그리드 설정으로 헤더명·편집 여부·입력 방식(콤보 ETC5 · 자유 입력)을 정한다(LOGISPRD-16194)
  const etc5Options = useMemo(() => toCodeOptions(combos?.ETC5), [combos])
  const { data: etc5Setting } = useReturnInboundEtc5Setting(ownShipperCode)
  /**
   * 업로드 헤더 → 키. 화주 헤더명(재고상태 등)도 정불구분으로 받는다 — 1.0 업로드는 화면 헤더명으로 맞췄다.
   * 화주 헤더명이 이미 표 제목에 있으면(정불구분 자신 또는 다른 컬럼 제목) 별칭을 더하지 않고 기존 매핑을 쓴다.
   */
  const titleToKey = useMemo(
    () => (etc5Setting?.header && !TITLE_TO_KEY.has(etc5Setting.header) ? new Map([...TITLE_TO_KEY, [etc5Setting.header, ETC5_KEY]]) : TITLE_TO_KEY),
    [etc5Setting],
  )
  // WH_NM 의 'ALL'(전체) 행은 저장 가능한 센터가 아니다(GR01 준용)
  const whNmOptions = useMemo(() => toSelectOptions(excludeAllOption(combos?.WH_NM)), [combos])
  const downloadReasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])
  // 조회영역 거래유형·불량구분 — 값은 콤보 코드 그대로 BE 로 간다('NULL' = 온라인·양품 → 빈 값 조건, LOGISPRD-13566). '전체'(빈 값)는 뷰가 붙이고 조건 없음
  const lot5FilterOptions = useMemo(() => toSelectOptions(combos?.LOT_ATTR5), [combos])
  const lot4FilterOptions = useMemo(() => toSelectOptions(combos?.LOT_ATTR4), [combos])
  /** 화주 고정 계정의 화주유형 선택지 — 고른 화주의 유형 하나. 옵션 슬롯은 렌더마다 불리므로 화주명별로 참조를 고정한다 */
  const lockedTypeOptions = useMemo(
    () =>
      new Map(
        selectableNames.map((name) => {
          const [type] = lockedServiceTypes([name])
          return [name, type ? [{ value: type, label: type }] : EMPTY_OPTIONS] as const
        }),
      ),
    [selectableNames, lockedServiceTypes],
  )

  /** 화주유형 → 화주명 선택지('' = 전체 유형). 뷰가 렌더마다 옵션 슬롯을 부르므로 화주 목록이 바뀔 때만 한 번 만든다(사전 리뷰) */
  const shipperNameOptionsByType = useMemo(() => {
    const byType = new Map<string, Set<string>>([['', new Set<string>()]])
    shippers.forEach((s) => {
      if (!s.shipperName) return
      byType.get('')?.add(s.shipperName)
      byType.set(s.logisticsServiceType, (byType.get(s.logisticsServiceType) ?? new Set<string>()).add(s.shipperName))
    })
    return new Map([...byType].map(([type, names]) => [type, [...names].map((name): Option => ({ value: name, label: name }))]))
  }, [shippers])

  /** 기본 조회조건 — 화주 = 로그인 화주(예: MUSINSA → M1P/MUSINSA, 재고통합 대상 계정은 MUSINSA), 입고예정일자 = 오늘-7일~오늘 */
  const defaultFilter = useCallback(
    (): Record<string, unknown> => ({
      service_type: isFreeShipper ? (ownServiceType ? [ownServiceType] : []) : lockedServiceTypes([]),
      shipper_name: defaultShipperName ? [defaultShipperName] : [],
      PLN_DATE: defaultPlnDate(),
    }),
    [isFreeShipper, ownServiceType, lockedServiceTypes, defaultShipperName],
  )

  // 조회조건 세션 유지 — [조회] 때 메뉴 세션·URL 에 남기고(reload 로 탭바 갱신) 마운트 때 URL 에서 한 번만 읽는다(rgr02V2 방식)
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  const [restored] = useState(() => fromStored(parsedQs))
  /**
   * filterFallback 을 한 번만 쓰게 하는 잠금 — 아래 슬롯 주석 참고. 처음 값:
   *  - 남겨 둔 조건 없이 진입: 켜진 채 시작(로그인 화주로 한 번 채운다).
   *  - 자유 화주 계정이 남겨 둔 조건(URL·세션)으로 진입: 꺼진 채 시작(그 조건이 곧 초기값 — 사람이 비운 칸을 채우지 않는다).
   *  - 화주 고정 계정이 남겨 둔 조건으로 진입: 켜진 채 시작 — 화주유형 칸이 잠겨 있어 사람이 비울 수 없고, 화주유형은 화주 목록이 와야 안다(moms-fe #843 리뷰).
   */
  const fallbackArmed = useRef(!restored || !isFreeShipper)
  const persistSearch = (query: Rgr01SearchQuery) => {
    MenuStorage.menuOnSession.setSearch(query)
    menuSession.reload()
    replaceQuery(query)
  }

  /** 조회영역 값 → 조회 조건. 화주 고정 계정은 고를 수 있는 화주와 그 유형으로만 조회한다. 고른 화주를 바꾸지 않는다 */
  const toParams = useCallback(
    (fl: Record<string, unknown>): ReturnInboundParams => {
      const [from, to] = (fl.PLN_DATE as [string, string] | undefined) ?? []
      const lockedNames = lockedShipperNames(list(fl.shipper_name))
      return {
        serviceTypes: isFreeShipper ? list(fl.service_type) : lockedServiceTypes(lockedNames),
        shipperNames: isFreeShipper ? list(fl.shipper_name) : lockedNames,
        plnDateFrom: from?.replace(/-/g, ''),
        plnDateTo: to?.replace(/-/g, ''),
        ordOptNos: list(fl.ORD_OPT_NO),
        refNo2s: list(fl.REF_NO3),
        trsInvoiceNos: list(fl.TRS_INVOICE_NO),
        // 1.0 은 양품·온라인을 골라도 조건이 붙지 않았다(콤보 'NULL'→null→생략). 2.0 은 그 값만 보기를 지원한다(LOGISPRD-13567). '전체'('')는 조건 없음
        lotAttr4: text(fl.LOT_ATTR4) || undefined,
        lotAttr5: text(fl.LOT_ATTR5) || undefined,
      }
    },
    [isFreeShipper, lockedShipperNames, lockedServiceTypes],
  )

  // ── 조회·표 행 ──────────────────────────────────────────────
  const [searchParams, setSearchParams] = useState<ReturnInboundParams | null>(null)
  const { data, refetch, isFetching } = useReturnInboundList(searchParams)
  // search 가 flushSync 로 조건을 커밋한 직후 새 키의 refetch 를 잡도록 렌더마다 갱신한다(GR01 준용)
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const [rows, setRows] = useState<ReturnInboundRow[]>([])

  /** 조회 결과를 표로 옮긴다 — 편집분과 업로드 신규 행은 여기서 버려진다(그게 [조회] 의 뜻이다) */
  const syncRows = useCallback((items: ReturnArrivalNoticeItem[]) => {
    setRows(items.filter(isReturnItem).map(toRow))
  }, [])
  useEffect(() => {
    if (data) syncRows(data)
  }, [data, syncRows])

  /**
   * 조회 실행 — 같은 조건이어도 서버에 다시 묻는다(1.0 동작). 결과가 이전과 같으면 react-query 가 리렌더를 건너뛰어 위 effect 가 돌지 않으니
   * 받은 결과로 표를 직접 되돌린다. 조건이 방금 바뀐 경우 refetch 가 이전 키를 잡지 않도록 flushSync 로 먼저 커밋한다(GR01 준용).
   */
  const runSearch = useCallback(
    async (next: ReturnInboundParams) => {
      flushSync(() => setSearchParams(next))
      // throwOnError — react-query 의 refetch 는 기본으로 실패를 삼키고 결과로 돌려준다. 던지게 해야 [조회]가 실패를 알아 뷰가 페이저·선택을 그대로 둔다
      //  (옛 화면: 실패하면 표·선택이 그대로). 안내는 전역 QueryCache 알림이 한다(옛 화면과 같다 — 여기서 따로 띄우지 않는다).
      const res = await refetchRef.current?.({ cancelRefetch: false, throwOnError: true })
      if (res?.data) syncRows(res.data)
    },
    [syncRows],
  )
  const reload = () => {
    if (searchParams) runSearch(searchParams).catch(() => undefined)
  }

  // 진입 1회 — 화주 목록이 와야 자기 화주유형을 안다. 도착하면 기본 조건(남겨 둔 조건이 있으면 그 조건)으로 조회한다(1.0 은 진입 즉시 조회).
  // 화주 고정 계정은 URL 을 손대 남의 화주로 들어와도 고를 수 있는 화주가 아니면 기본 화주로 덮는다(유형은 toParams 가 고른 화주로 정한다). 뷰의 조회영역 값은 filterInitial·filterFallback 이 같은 값으로 채운다.
  const didInit = useRef(false)
  useEffect(() => {
    if (didInit.current || !shippersFetched) return
    didInit.current = true
    const def = defaultFilter()
    const lock = isFreeShipper ? {} : { shipper_name: lockedShipperNames(list(restored?.shipper_name)) }
    const fl = restored ? { ...def, ...restored, ...lock } : def
    const params = toParams(fl)
    if (hasShipperCondition(params)) setSearchParams(params)
  }, [shippersFetched, defaultFilter, toParams, restored, isFreeShipper])

  // ── 첨부·동영상(공용 MediaModal — 뷰 모달 대신) ───────────────
  // 첫 업로드로 방금 생긴 fileId — 다음 조회 전까지 data 에는 없다(rgr04 준용)
  const [extraFiles, setExtraFiles] = useState<{ shipper: string; fileId: string }[]>([])
  const fileGroups = useMemo(() => {
    const byShipper = new Map<string, Set<string>>()
    const add = (shipper: string | null, fileId: string | null) => {
      if (!shipper || !fileId) return
      byShipper.set(shipper, (byShipper.get(shipper) ?? new Set<string>()).add(fileId))
    }
    ;(data ?? []).filter(isReturnItem).forEach((item) => add(item.sprNm, item.fileId))
    extraFiles.forEach((f) => add(f.shipper, f.fileId))
    return [...byShipper].map(([shipper, ids]) => ({ shipper, fileIds: [...ids].sort((a, b) => a.localeCompare(b)) }))
  }, [data, extraFiles])
  // ponytail: 파일 목록 API 가 화주 단위라 화주마다 GET 1번(1.0 은 조회 응답에 파일을 실어 섞여도 됐다 — PM 결정 14).
  const fileQueries = useQueries({
    queries: fileGroups.map((g) => ({
      queryKey: ['rgr16', 'MEDIA', g.shipper, g.fileIds],
      queryFn: async () => (await getCommonFiles(g.shipper, g.fileIds)).data.items,
    })),
  })
  const filesByFileId = useMemo(
    () => new Map(fileQueries.flatMap((q) => (q.data ?? []).map((item): [string, CommonFileItemRes] => [item.fileId, item]))),
    [fileQueries],
  )
  const [mediaTarget, setMediaTarget] = useState<{ kind: 'video' | 'attach'; row: ReturnInboundRow } | null>(null)
  /** 셀 클릭 가드 — 뷰의 미디어 모달을 열지 않고(false) 공용 MediaModal 을 그 행으로 연다 */
  const openMedia = (kind: 'video' | 'attach', target: CellTarget): boolean => {
    const record = rows.find((r) => r._rowKey === text(target.row._rowKey))
    if (!record) return false
    // 저장 전 행은 id 가 없어 파일을 붙일 대상이 없다
    if (record._isNew) notice.invalid('저장한 뒤에 첨부할 수 있습니다.')
    else setMediaTarget({ kind, row: record })
    return false
  }
  /** 첫 업로드면 서버가 새로 만든 fileId 를 그 행에 심고 목록 조회 대상에 넣는다 — 안 그러면 다음 조회 전까지 안 보인다 */
  const onMediaUploaded = (newFileId?: string) => {
    const target = mediaTarget?.row
    if (newFileId && target) {
      setExtraFiles((prev) => [...prev, { shipper: target._src?.sprNm ?? target.SPR_NM, fileId: newFileId }])
      setRows((prev) => prev.map((r) => (r._rowKey === target._rowKey ? { ...r, _fileId: newFileId } : r)))
      setMediaTarget((prev) => (prev ? { ...prev, row: { ...prev.row, _fileId: newFileId } } : prev))
    }
    queryClient.invalidateQueries({ queryKey: ['rgr16', 'MEDIA'] }).catch(() => undefined)
  }

  /**
   * 뷰에 넘기는 행 — key = 행키(재조회·페이지 이동 뒤에도 체크·셀 패치가 같은 행에 붙는다), 첨부·동영상 칸은 파일 수.
   * 엑셀 업로드 행은 DS 신규 행 표식(`__new`)을 달아 필수 빈 칸을 편집 전부터 빨갛게 그린다(편집 콜백에서 다시 뗀다).
   */
  const viewRows = useMemo<Row[]>(
    () =>
      rows.map((r) => {
        const files = r._fileId ? filesByFileId.get(r._fileId) : undefined
        const view = { ...r, key: r._rowKey, ATCH_FILE: files?.attachments.length ?? 0, VIDEO: files?.videos.length ?? 0 }
        return r._isNew ? withNewMarker(view) : view
      }),
    [rows, filesByFileId],
  )
  /** 뷰가 준 행(체크 선택) → 화면이 들고 있는 최신 행. 편집값은 ROWS_rowsChange 로 이미 rows 에 들어와 있다 */
  const pickRows = (selected: Row[]): ReturnInboundRow[] => {
    const keys = new Set(selected.map((r) => text(r._rowKey)))
    return rows.filter((r) => keys.has(r._rowKey))
  }

  // ── 헬퍼 모달 후보(모달이 열려 있고 [조회]를 눌렀을 때만 — 옛 search*Helper) ──────────────
  const [helperOpen, setHelperOpen] = useState({ shipper: false, barcode: false })
  const [shipperQuery, setShipperQuery] = useState<{ type: string; name: string } | null>(null)
  const [barcodeQuery, setBarcodeQuery] = useState<{ shipperCode: string; brandName: string; itemCode: string } | null>(null)
  const shipperHelper = useQuery<ShipperItem[], Error>({
    queryKey: returnInboundQueryKeys.helperShipper(shipperQuery),
    queryFn: async () => {
      // 화주 고정 계정은 자기 화주만 찾는다
      const freeNames = shipperQuery?.name ? [shipperQuery.name] : []
      const res = await getShipperListV2({
        shipperNames: isFreeShipper ? freeNames : [helperShipperName],
        logisticsServiceTypes: shipperQuery?.type ? [shipperQuery.type] : [],
        page: 0,
        size: 1000,
      })
      return res?.data?.items ?? []
    },
    enabled: helperOpen.shipper && shipperQuery !== null,
    meta: { noErrorMessage: true },
  })
  const barcodeHelper = useQuery<BarcodeOption[], Error>({
    queryKey: returnInboundQueryKeys.helperBarcode(barcodeQuery),
    queryFn: () => fetchBarcodeOptions(barcodeQuery ?? { shipperCode: '' }),
    // 조건 없이 부르면 화주의 바코드 전량이 페이징 없이 온다 — 브랜드·상품코드 중 하나는 있어야 나간다
    enabled: helperOpen.barcode && barcodeQuery !== null && Boolean(barcodeQuery.brandName || barcodeQuery.itemCode),
    meta: { noErrorMessage: true },
  })
  // 헬퍼 조회 실패는 실패 알럿으로 알린다(2.0 안내 기준 — 전역 알림창은 끈다: meta.noErrorMessage)
  useEffect(() => {
    if (shipperHelper.error) notice.failure(apiErrMessage(shipperHelper.error))
  }, [shipperHelper.error, shipperHelper.errorUpdatedAt])
  useEffect(() => {
    if (barcodeHelper.error) notice.failure(apiErrMessage(barcodeHelper.error))
  }, [barcodeHelper.error, barcodeHelper.errorUpdatedAt])
  const shipperHelperRows = useMemo<Row[]>(
    () => (shipperHelper.data ?? []).map((s) => ({ ...s, key: s.shipperCode, service_type: s.logisticsServiceType, shipper_name: s.shipperName, SPR_NM: s.shipperCode })),
    [shipperHelper.data],
  )
  const barcodeHelperRows = useMemo<Row[]>(
    () => (barcodeHelper.data ?? []).map((b, i) => ({ ...b, key: `${b.barcode}-${i}`, BRD_NM: b.brandName, GDS_CD: b.itemCode, BARCODE: b.barcode })),
    [barcodeHelper.data],
  )
  /**
   * 모달 [조회] 적용 조건(rows 슬롯의 q.filters — 누를 때마다 새 객체) → 질의 상태. 슬롯은 **뷰의 렌더 중에** 불리므로(뷰만 다시 그려질 때는 이
   * 컴포넌트의 effect 가 돌지 않는다) 조건 객체가 바뀐 것만 기록하고, 상태 갱신은 렌더가 끝난 뒤(microtask)로 미룬다. 바코드는 연 셀(q.target)의 행 화주로 찾는다.
   */
  const appliedShipper = useRef<Record<string, unknown> | undefined>(undefined)
  const appliedBarcode = useRef<{ filters: Record<string, unknown> | undefined; target?: CellTarget }>({ filters: undefined })
  const [appliedSeq, setAppliedSeq] = useState(0)
  const handledBarcode = useRef<Record<string, unknown> | undefined>(undefined)
  const handledShipper = useRef<Record<string, unknown> | undefined>(undefined)
  useEffect(() => {
    // 화주 조건이 새로 적용됐을 때만 — 같은 값으로 [조회]를 다시 누르면 쿼리 키가 같아 요청이 안 나가므로 직접 다시 받는다(옛 화면은 누를 때마다 조회)
    if (handledShipper.current !== appliedShipper.current) {
      handledShipper.current = appliedShipper.current
      const f = appliedShipper.current ?? {}
      const next = f.__applied ? { type: text(f.service_type).trim().toUpperCase(), name: text(f.shipper_name).trim() } : null
      if (next && shipperQuery && next.type === shipperQuery.type && next.name === shipperQuery.name) shipperHelper.refetch().catch(() => undefined)
      setShipperQuery(next)
    }
    const b = appliedBarcode.current
    // 바코드 조건이 그대로면(화주 헬퍼만 바뀐 경우) 다시 안내·질의하지 않는다
    if (handledBarcode.current === b.filters) return
    handledBarcode.current = b.filters
    const bf = b.filters ?? {}
    if (!bf.__applied) {
      setBarcodeQuery(null)
      return
    }
    const brandName = text(bf.BRD_NM).trim()
    const itemCode = text(bf.GDS_CD).trim()
    // 조건 없이는 질의 상태를 바꾸지 않는다 — 바꾸면 쿼리 키가 빈 조건으로 넘어가 직전 후보가 지워지고, 같은 조건 재조회 판정도 어긋난다(옛 화면: 안내 뒤 return)
    if (!brandName && !itemCode) {
      notice.invalid('브랜드 또는 상품코드를 입력해 주세요.')
      return
    }
    // 저장과 같은 화주로 찾는다 — 저장된 행은 원본 화주로 바코드→brandId 를 조회하므로(saveShipper) 편집된 화주의 바코드를 고르면 brandId 가 비워진다
    const targetRow = rows.find((r) => r._rowKey === text(b.target?.row._rowKey))
    const rowShipper = targetRow?._src ? targetRow._src.sprNm : targetRow?.SPR_NM
    const nextBarcode = { shipperCode: rowShipper || ownShipperCode, brandName, itemCode }
    const sameBarcode = barcodeQuery && barcodeQuery.shipperCode === nextBarcode.shipperCode && barcodeQuery.brandName === brandName && barcodeQuery.itemCode === itemCode
    if (sameBarcode && (brandName || itemCode)) barcodeHelper.refetch().catch(() => undefined)
    setBarcodeQuery(nextBarcode)
    // 의존성은 appliedSeq 하나 — 모달 [조회]·Enter 로 조건이 **새로 적용될 때만** 돈다. 이때 읽는 rows·질의 상태는 그 렌더의 최신 값이다
    //  (effect 는 의존성이 바뀐 렌더의 클로저로 실행된다 — 헬퍼가 열린 채 목록을 다시 조회해도 다음 적용은 새 rows 로 화주를 찾는다).
    //  rows 를 의존성에 넣으면 셀 편집마다 다시 돌아 같은 조건으로 재질의·경고가 반복된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSeq])
  const trackApplied = (kind: 'shipper' | 'barcode', filters: Record<string, unknown> | undefined, target?: CellTarget) => {
    if (kind === 'shipper') {
      if (appliedShipper.current === filters) return
      appliedShipper.current = filters
    } else {
      if (appliedBarcode.current.filters === filters) return
      appliedBarcode.current = { filters, target }
    }
    queueMicrotask(() => setAppliedSeq((n) => n + 1))
  }

  // ── 저장·삭제·확정 ──────────────────────────────────────────
  const saveMutation = useSaveReturnInbounds()
  const deleteMutation = useDeleteReturnInbounds()
  const confirmMutation = useConfirmReturnInbounds()
  const busy = saveMutation.isPending || deleteMutation.isPending || confirmMutation.isPending
  // 상태 기반 잠금은 mutate 가 시작돼야 켜진다 — 확인창이 닫힌 직후 한 틱을 ref 로 막는다(GR01 준용).
  // 저장이 끝나기 전 확정이 나가면 반영 안 된 값으로 입고플랜이 만들어지고, 확정은 되돌릴 수 없다.
  const actionLock = useRef(false)
  const [skuError, setSkuError] = useState<SkuUnmappedError | null>(null)
  /** 요청 하나를 잠금 안에서 돌린다 — 실패는 안내(SKU 미매핑은 전용 모달) 뒤 다시 던진다(뷰가 확인창·선택을 유지) */
  const withLock = async <T,>(fn: () => Promise<T>): Promise<T> => {
    if (actionLock.current || busy) throw new Error('busy')
    actionLock.current = true
    try {
      return await fn()
    } catch (e) {
      const skuUnmapped = asSkuUnmapped(e)
      if (skuUnmapped) setSkuError(skuUnmapped)
      else notice.failure(apiErrMessage(e))
      throw e
    } finally {
      actionLock.current = false
    }
  }
  // 확인창이 뜨기 전 더블클릭이 확인창을 두 개 띄우지 않게 — 옛 화면의 confirmModalOpen 가드 자리(뷰의 확인창은 닫힘을 알려 주지 않아 시간으로 막는다)
  const confirmOpenedAt = useRef(0)
  const confirmJustOpened = (): boolean => Date.now() - confirmOpenedAt.current < CONFIRM_REOPEN_GUARD_MS
  /** 삭제·확정의 화주 — 편집 셀 값이 아니라 조회 원본이다(BE 가 SPR_NM 으로 대상을 거른다) */
  const serverShipper = (r: ReturnInboundRow) => r._src?.sprNm ?? ''

  // 화면 안내용 검사다. 최종 권한은 서버가 인증 작업자와 DB 원본으로 확인한다.
  const ownershipError = (selected: ReturnInboundRow[]): string | undefined => {
    const email = AuthStorage.user.get().email?.toLowerCase()
    if (email !== PROTECTED_ACCOUNT_EMAIL) return undefined
    if (selected.some((r) => !r._isNew && serverShipper(r) === 'INOOINT' && r._src?.insBy?.toLowerCase() !== email)) {
      return '본인이 등록한 반품입고만 수정·삭제할 수 있습니다. 등록자가 없거나 다른 사람이 등록한 행이 포함되어 있습니다.'
    }
    return undefined
  }

  // ── 엑셀 업로드 ─────────────────────────────────────────────
  // 렌더에 쓰지 않고 행키 생성에만 쓴다 — 연속 업로드 때 키가 겹치지 않게
  const importSeq = useRef(0)

  // ── 뷰 배선(Wiring) — 배선 객체는 매 렌더 새로 만든다(뷰가 렌더 첫머리에 props.wiring 을 모듈 홀더에 대입한다 — 문서 51 ⑪) ──
  const wiring: Wiring = {
    permission: usePermissionSlot('rgr16'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr01Table: coreTableLayoutSlot('RGR16_HEAD') },
    rows: {
      // { rows, loading } — total 을 주지 않아 클라이언트 페이징(전건 10,000 단위, 목록 검색·자르기는 뷰가 한다)
      ROWS: () => ({ rows: viewRows, loading: isFetching }),
      // 화주 헬퍼 후보 — 모달 [조회]가 적용한 조건(q.filters.__applied)이 있을 때만 보인다
      ROWS2: (_demo, q) => {
        trackApplied('shipper', q.filters)
        return { rows: q.filters?.__applied ? shipperHelperRows : [], loading: shipperHelper.isFetching }
      },
      // 바코드 헬퍼 후보 — 연 셀(q.target)의 행 화주로 찾는다
      ROWS3: (_demo, q) => {
        trackApplied('barcode', q.filters, q.target)
        return { rows: q.filters?.__applied ? barcodeHelperRows : [], loading: barcodeHelper.isFetching }
      },
    },
    actions: {
      /** [조회] — 화주 조건이 없으면 조회하지 않는다(false → 뷰가 페이저·선택을 되돌리지 않는다). 조건은 메뉴 세션·URL 에 남긴다 */
      search: async (filters) => {
        const params = toParams(filters)
        if (!hasShipperCondition(params)) {
          notice.invalid('화주유형, 화주명 중 하나는 필수입니다.')
          return false
        }
        persistSearch(toStored(filters))
        try {
          await runSearch(params)
        } catch {
          // 조회 실패 — 안내는 전역 알림(QueryCache)이 이미 띄웠다. false 로 알려 뷰가 페이저·선택을 되돌리지 않게 한다(표는 직전 조회 결과 그대로)
          return false
        }
        return undefined
      },
      /** [저장] — 화주 혼재·필수값을 먼저 막고(안내 뒤 throw) 저장한 뒤 다시 조회한다. 성공 토스트("저장했습니다.")는 뷰가 낸다 */
      rgr01SaveButton: async (ctx) => {
        // 받는 중이면 곧 덮일 행이다 — throw 로 막아 선택을 남긴다(안내는 guardFetching 이 띄운다)
        if (!guardFetching(isFetching)) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const selected = pickRows(ctx.selected)
        const denied = ownershipError(selected)
        if (denied) throw rowInvalidError(denied)
        const mixed = mixedShippers(selected.map(saveShipper))
        if (mixed.length) throw rowInvalidError(`화주가 다른 행이 섞여 있습니다 (${mixed.join(', ')}). 화주별로 나눠 저장하세요.`)
        const invalid = validateRows(selected, REQUIRED_FIELDS)
        if (invalid.length) {
          // 행을 짚는 검증 — 알럿 한 장에 줄을 나눠 싣는다(문구는 그대로 · 알럿을 여러 장 겹쳐 띄우지 않는다)
          const lines = invalid.slice(0, 5)
          if (invalid.length > 5) lines.push(`이 밖에 ${invalid.length - 5}건의 필수값 오류가 더 있습니다.`)
          throw rowInvalidError(lines.join('\n'))
        }
        await withLock(() => saveMutation.mutateAsync({ shipper: saveShipper(selected[0]), rows: selected }))
        reload()
      },
      /** 편집 셀 반영 — onRowsChange 는 현재 페이지·검색 결과 전체를 돌려준다. 행키로 대조해 바뀐 행만 덮는다(헬퍼 모달 패치도 이 길로 온다) */
      ROWS_rowsChange: (next) => {
        const byKey = new Map(next.map((r) => [text(r._rowKey), withoutNewMarker(r) as unknown as ReturnInboundRow]))
        setRows((prev) =>
          prev.map((r) => {
            const changed = byKey.get(r._rowKey)
            return changed ? followBarcode(r, changed) : r
          }),
        )
      },
      /** 확정 확인 — 요청 뒤 재조회. 성공 토스트("반품입고를 확정했습니다.")는 뷰가 낸다 */
      rgr01ConfirmModal_ok: async (ctx) => {
        const selected = pickRows(ctx.selected)
        // 확인창이 겹쳐 떴을 때 두 번째 [확인] — 첫 요청이 끝나 선택이 비었다. 요청 없이 확인창만 남긴다
        if (!selected.length) throw new Error('no selection')
        await withLock(() => confirmMutation.mutateAsync({ shipper: serverShipper(selected[0]), ids: selected.map((r) => r._id) }))
        reload()
      },
      /** 삭제 확인 — 서버 삭제가 끝난 뒤에 신규 행을 뺀다(먼저 빼면 서버가 실패했을 때 저장 안 한 신규 행까지 사라진다). 건수 토스트는 반환값으로 */
      rgr01DeleteConfirmModal_ok: async (ctx) => {
        const selected = pickRows(ctx.selected)
        const denied = ownershipError(selected)
        if (denied) throw rowInvalidError(denied)
        const saved = selected.filter((r) => !r._isNew)
        const affected = saved.length
          ? await withLock(() => deleteMutation.mutateAsync({ shipperCode: serverShipper(saved[0]), ids: saved.map((r) => r._id) }))
          : 0
        // 저장 전 신규 행은 서버에 없다 — 표에서만 뺀다
        const newKeys = new Set(selected.filter((r) => r._isNew).map((r) => r._rowKey))
        if (newKeys.size) setRows((prev) => prev.filter((r) => !newKeys.has(r._rowKey)))
        if (!saved.length) return false
        reload()
        return `${affected}건 삭제되었습니다.`
      },
      /** 엑셀 다운로드 — 사유코드·상세사유(5자 이상)·파일 비밀번호(BE 규칙)를 막고(throw — 뷰가 [확인]을 입력이 유효할 때만 켜므로 안전망. 모달은 누르는 즉시 닫힌다) 조회한 조건 그대로 내려받는다 */
      rgr01DownloadModal_ok: async (ctx) => {
        const reasonCode = text(ctx.form?.reason)
        const detailedReason = text(ctx.form?.detail).trim()
        const password = text(ctx.form?.password)
        if (!searchParams) throw invalidError('조회한 뒤에 다운로드할 수 있습니다.')
        if (!reasonCode) throw invalidError('사유코드를 선택해 주세요.')
        if (detailedReason.length < REASON_DETAIL_MIN_LENGTH) throw invalidError(`상세사유는 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`)
        if (!isValidDownloadPassword(password)) throw invalidError('파일 비밀번호는 8자 이상, 대문자·소문자·숫자·특수문자를 모두 포함해야 합니다(공백 불가).')
        try {
          await downloadReturnInboundsExcel(searchParams, { reasonCode, detailedReason, password })
        } catch (e) {
          throw failureError(await blobErrMessage(e))
        }
        return '다운로드 완료'
      },
      /**
       * 엑셀 업로드 — 1.0 TOOL_IMP 파리티: 클라 파싱 → 표를 통째로 교체 → 사용자가 검토 후 [저장].
       * 덧붙이지 않는다 — 같은 파일을 두 번 올리면 신규 행이 두 벌 쌓이고 둘 다 생성 요청으로 나간다(GR01 결정 09).
       */
      rgr01UploadModal_ok: async (ctx) => {
        const file = (ctx.form?.file ?? (ctx.form?.files as unknown[] | undefined)?.[0]) as File | undefined
        if (!file) throw invalidError('업로드할 파일을 선택해 주세요.')
        // 조회 응답이 뒤늦게 오면 방금 올린 행을 덮는다 — 조회가 끝난 뒤에 받는다
        if (isFetching) throw failureError('조회가 끝난 뒤에 업로드해 주세요.')
        let imported: Awaited<ReturnType<typeof parseReturnInboundExcel>>
        try {
          imported = await parseReturnInboundExcel(file, {
            titleToKey,
            codeMappers: { LOT_ATTR4: makeCodeMapper(lot4Options), LOT_ATTR5: makeCodeMapper(lot5Options) },
          })
        } catch (e) {
          return failAndClose(rowInvalidError(e instanceof ExcelImportError ? e.message : '엑셀 파일을 읽을 수 없습니다.'))
        }
        if (!imported.length) return failAndClose(invalidError('가져올 데이터 행이 없습니다.'))
        const start = importSeq.current
        importSeq.current += imported.length
        // 화주코드 칸이 없으면 로그인 화주로 채운다(1.0 업로드 기본값)
        setRows(imported.map((item, i) => ({ ...emptyReturnInboundRow(`imp-${start + i}`), SPR_NM: ownShipperCode, ...item }) as ReturnInboundRow))
        callToAddImportLog({ menuCode: 'RGR16', apiName: 'RGR16_LIST_IMPORT', rowCount: imported.length })
        noticeIfUploadExceedsPage(imported.length)
        return `${imported.length}행을 가져왔습니다. 검토 후 저장하세요.`
      },
      // 첨부·동영상은 cellOpens 가 뷰 모달을 열지 않는다 — 이 두 슬롯은 불리지 않는다(계약상 자리만)
      rgr01VideoModal_ok: () => {},
      rgr01AttachModal_ok: () => {},
      /** 화주 헬퍼 [선택] — 화주코드뿐 아니라 화주명·화주유형도 함께 그 행에 넣는다(반환 객체 = 셀 패치) */
      rgr01ShipperHelperModal_ok: (ctx) => {
        const picked = ctx.picked?.[0]
        if (!picked) throw invalidError('화주를 선택해 주세요.')
        return { SPR_NM: picked.SPR_NM, shipper_name: picked.shipper_name, service_type: picked.service_type }
      },
      /**
       * 바코드 헬퍼 [선택] — 브랜드·스타일넘버도 함께 바꾼다. 바코드만 바꾸면 BE 가 요청값을 그대로 저장해(ArrivalNoticeService.update
       * `brdNm = item.brandName`) 원장에 옛 상품의 브랜드·스타일넘버가 남는다. 두 칸을 표에서 손으로 고칠 수도 있지만 바코드를 바꿀 때는 이 값으로 맞춘다.
       */
      rgr01BarcodeHelperModal_ok: (ctx) => {
        const picked = ctx.picked?.[0]
        if (!picked) throw invalidError('바코드를 선택해 주세요.')
        return { BARCODE: picked.BARCODE, BRD_NM: picked.brandName ?? '', STL_NO: picked.styleNumber ?? '' }
      },
    },
    /** 요청 진행 중 — 저장·삭제·확정 버튼을 잠근다 */
    useBusy: () => busy,
    deleteConfirmMessage: (ctx) => {
      const selected = pickRows(ctx.selected)
      const saved = selected.filter((r) => !r._isNew).length
      const unsaved = selected.length - saved
      if (!saved) return `미저장 행 ${unsaved}건은 화면에서 제거됩니다. DB에 저장된 데이터는 삭제하지 않습니다. 계속하시겠습니까?`
      const unsavedMessage = unsaved ? ` 미저장 행 ${unsaved}건은 화면에서 제거됩니다.` : ''
      return `저장된 반품입고 ${saved}건이 삭제됩니다.${unsavedMessage} 계속하시겠습니까?`
    },
    opens: {
      /** 삭제 전 가드 — 화주 혼재·건수 상한(옛 onDelete) */
      rgr01DeleteButton: (ctx) => {
        if (actionLock.current || busy || confirmJustOpened()) return false
        if (!guardFetching(isFetching)) return false
        const selected = pickRows(ctx.selected)
        const denied = ownershipError(selected)
        if (denied) {
          notice.rowInvalid(denied)
          return false
        }
        const saved = selected.filter((r) => !r._isNew)
        const mixed = mixedShippers(saved.map(serverShipper))
        if (mixed.length) {
          notice.rowInvalid(`화주가 다른 행이 섞여 있습니다 (${mixed.join(', ')}). 화주별로 나눠 삭제하세요.`)
          return false
        }
        if (saved.length > DELETE_IDS_MAX) {
          notice.invalid(`한 번에 ${DELETE_IDS_MAX.toLocaleString()}건까지 삭제할 수 있습니다.`)
          return false
        }
        confirmOpenedAt.current = Date.now()
        return true
      },
      /** 확정 전 가드 — 미저장 행·화주 혼재(옛 onConfirm) */
      rgr01ConfirmButton: (ctx) => {
        if (actionLock.current || busy || confirmJustOpened()) return false
        if (!guardFetching(isFetching)) return false
        const selected = pickRows(ctx.selected)
        if (selected.some((r) => r._isNew)) {
          notice.invalid('저장하지 않은 행이 있습니다. 저장한 뒤 확정하세요.')
          return false
        }
        const mixed = mixedShippers(selected.map(serverShipper))
        if (mixed.length) {
          notice.rowInvalid(`화주가 다른 행이 섞여 있습니다 (${mixed.join(', ')}). 화주별로 나눠 확정하세요.`)
          return false
        }
        confirmOpenedAt.current = Date.now()
        return true
      },
      /** 다운로드는 조회한 조건으로 내려받는다 — 조회 전에는 열지 않는다 */
      rgr01ExcelButton: () => {
        if (!guardFetching(isFetching)) return false
        if (searchParams) return true
        notice.invalid('조회한 뒤에 다운로드할 수 있습니다.')
        return false
      },
    },
    /** 헬퍼 모달 열림 — 열려 있는 동안에만 후보를 조회한다(옛 shipperHelper/barcodeHelper 상태) */
    onModal: (open) => {
      setHelperOpen((s) =>
        s.shipper === open.rgr01ShipperHelperModal && s.barcode === open.rgr01BarcodeHelperModal
          ? s
          : { shipper: open.rgr01ShipperHelperModal, barcode: open.rgr01BarcodeHelperModal },
      )
    },
    /** 비고·송장·수하인·판매업체·반송번호·브랜드는 입력칸으로 열고, 정불구분 칸은 화주 설정(헤더명·입력 방식)을 입혀 연다 */
    columns: { rgr01Table: (columns) => applyEtc5Setting(openFreeTextColumns(columns), etc5Setting, etc5Options) },
    bulkInput: {
      ROWS: (demo) => demo.map((it) => (it.key === 'SPR_NM' ? { ...it, options: bulkShipperOptions, toPatch: bulkShipperPatch } : it)),
    },
    options: {
      // 화주유형 — 화주 고정 계정은 고른 화주의 유형 하나
      RGR01SHIPPER_TYPE_OPTIONS: (_demo, filters) =>
        isFreeShipper ? SHIPPER_TYPE_OPTIONS : (lockedTypeOptions.get(lockedShipperNames(list(filters?.shipper_name))[0]) ?? EMPTY_OPTIONS),
      // 화주명 — 고른 화주유형의 화주만. 화주 고정 계정은 목록이 고를 수 있는 화주뿐이라 유형으로 좁히지 않는다 — 좁히면 다른 화주(MUSINSA)를 고를 수 없다(gr02V2 준용)
      RGR01SHIPPER_NAME_OPTIONS: (_demo, filters) => shipperNameOptionsByType.get(isFreeShipper ? (list(filters?.service_type)[0] ?? '') : '') ?? [],
      RGR01LOTATTR5_OPTIONS: () => lot5FilterOptions,
      RGR01LOTATTR4_OPTIONS: () => lot4FilterOptions,
      ROWS_LOT_ATTR5: () => lot5Options,
      ROWS_LOT_ATTR4: () => lot4Options,
      ROWS_WH_NM: () => whNmOptions,
      REASONRADIO_OPTIONS: () => downloadReasonOptions,
    },
    /** 조회 기본값 — 로그인 화주명(재고통합 대상 계정은 MUSINSA) + 입고예정일자 오늘-7일~오늘. 화주유형은 화주 목록이 온 뒤 filterFallback 이 채운다. [초기화] 도 여기로 돌아간다 */
    filterDefaults: { rgr01FilterCard: (demo) => ({ ...demo, shipper_name: defaultShipperName ? [defaultShipperName] : [], PLN_DATE: defaultPlnDate() }) },
    /** 진입 초기값 — URL 에 남겨 둔 조건. 화주 고정 계정은 고를 수 있는 화주가 아니면 기본 화주로 덮는다 */
    filterInitial: {
      rgr01FilterCard: (defaults) => {
        if (!restored) return defaults
        // 화주유형은 기본값에 없다(화주 목록이 와야 안다) — 비워 두고 filterFallback 이 자기 화주유형으로 채운다. URL 의 남의 화주유형이 잠긴 칸에 남지 않게 한다
        const lock = isFreeShipper ? {} : { service_type: [], shipper_name: lockedShipperNames(list(restored.shipper_name)) }
        return { ...defaults, ...restored, ...lock }
      },
    },
    /**
     * 화주 목록이 온 뒤 비어 있는 화주유형·화주명을 로그인 화주로 채운다(값이 있는 칸은 뷰가 덮지 않는다).
     * **진입·[초기화] 직후 한 번만** — 뷰는 옵션이 바뀔 때마다 이 슬롯을 부르는데, 화주유형을 바꾸면 화주명 선택지가 바뀐다. 그때마다 채우면
     * 사용자가 비운 화주유형(×)·유형을 바꿔 비워진 화주명이 도로 로그인 화주로 돌아간다(옛 화면: 유형만으로·화주명만으로 조회할 수 있었다).
     * 남겨 둔 조건(URL·세션)으로 들어왔으면 그 조건이 곧 초기값이라 채우지 않는다 — 단 화주 고정 계정은 예외다(화주유형 칸이 잠겨 있고
     * filterInitial 이 비워 두므로 화주 목록이 온 뒤 한 번 채운다, fallbackArmed 참고). 해제는 렌더가 끝난 뒤로 미룬다(갱신 함수 안에서 두 번 불려도 같은 값).
     */
    filterFallback: {
      rgr01FilterCard: (current) => {
        if (!fallbackArmed.current || !shippersFetched) return undefined
        queueMicrotask(() => {
          fallbackArmed.current = false
        })
        // 화주 고정 계정의 화주유형은 칸에 든 화주(비었으면 기본 화주)의 유형이다
        const serviceTypes = isFreeShipper ? (ownServiceType ? [ownServiceType] : []) : lockedServiceTypes(list(current.shipper_name))
        return {
          ...(serviceTypes.length ? { service_type: serviceTypes } : {}),
          ...(defaultShipperName ? { shipper_name: [defaultShipperName] } : {}),
        }
      },
    },
    /** 첨부·동영상 — 뷰 모달 대신 공용 MediaModal(서버 업로드·내려받기). 헬퍼 모달은 뷰 것을 그대로 쓴다 */
    cellOpens: {
      rgr01AttachModal: (target) => openMedia('attach', target),
      rgr01VideoModal: (target) => openMedia('video', target),
    },
    /** 엑셀 업로드 뒤 가져온 행 전부 체크 — 옛 화면과 같다(검토 후 바로 [저장]) */
    selectAllAfterUpload: true,
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다 — 옛 화면의 disabled. 보이는 것만 잠그므로 값은 toParams·filterInitial 이 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15725)
     */
    filterDisabled: isFreeShipper ? undefined : { service_type: true, shipper_name: !canSelectShipper(scope) },
    /** [초기화] — 남겨 둔 조회 조건(세션·URL)도 지운다. 표는 그대로 둔다(옛 화면과 같다) */
    onReset: () => {
      fallbackArmed.current = true
      persistSearch({})
    },
  }

  return (
    <>
      <ReturnInboundPageView wiring={wiring} />
      {/* 뷰 밖 모달 — 뷰의 ConfigProvider 가 닿지 않아 같은 테마를 한 번 더 건다(포털 서체는 뷰가 body 에 건 클래스를 따른다) */}
      <core.ConfigProvider theme={pboLogisticsTheme}>
        <MediaModal
          open={Boolean(mediaTarget)}
          kind={mediaTarget?.kind ?? null}
          row={mediaTarget ? { id: mediaTarget.row._id, fileId: mediaTarget.row._fileId } : null}
          shipper={mediaTarget ? (mediaTarget.row._src?.sprNm ?? mediaTarget.row.SPR_NM) : ''}
          filesByFileId={filesByFileId}
          onUploaded={onMediaUploaded}
          onClose={() => setMediaTarget(null)}
        />
        <SkuUnmappedModal error={skuError} onClose={() => setSkuError(null)} />
      </core.ConfigProvider>
    </>
  )
}

export default withCoMingleReturns(ReturnInboundPage)
